import React, { useState, useEffect, useRef } from "react"
import { flushSync } from "react-dom"
import Autocomplete  from "./autocomplete"

const PATHNAME = '/geo/suggest'
const HEADERS  = {
  'Accept':           'application/json',
  'X-Requested-With': 'XMLHttpRequest',
  'Cache-Control':    'no-cache',
}

const THROTTLE = 200

// The suggestions are OpenStreetMap data (ODbL): it asks to be credited.
const ATTRIBUTION = '© OpenStreetMap'

const Address = props => {
  const [suggestions,    setSuggestions]    = useState([])
  const [currentTimeout, setCurrentTimeout] = useState(null)
  const [address, setAddress] = useState('')
  const [noMatch, setNoMatch] = useState(false)
  const [filled, setFilled] = useState(props.defaultValue)
  const wrapper = useRef(null)
  // A slow answer for "12 r" can land after "12 rue de Rivoli" and overwrite it.
  const pending = useRef(null)

  const buildURL = query => {
    const url = new URL(PATHNAME, window.location.origin)
    url.searchParams.set('q', query)
    return url
  }

  const onChange = query => {
    if (currentTimeout) clearTimeout(currentTimeout)

    setCurrentTimeout(
      setTimeout(() => {
        if (pending.current) pending.current.abort()
        const controller = new AbortController()
        pending.current  = controller

        fetch(buildURL(query), {
          headers:     HEADERS,
          credentials: 'same-origin',
          signal:      controller.signal
        })
        .then(response => response.ok ? response.json() : { suggestions: [] })
        .then(data => {
          const rows = data.suggestions || []
          setSuggestions(rows)
          setNoMatch(rows.length === 0)
        })
        .catch(() => {}) // aborted or offline: keep the previous suggestions
      }, THROTTLE)
    )
  }

  useEffect(() => {
    let addressEvent = new Event('address');
    let addressField = document.getElementById("address_field")
    if (address && addressField) {
      addressField.dispatchEvent(addressEvent);
    }
  }, [address])

  useEffect(() => () => { if (pending.current) pending.current.abort() }, [])

  // Filled from outside, the value must go through React or the next render undoes it.
  // Reset first: the same text twice (typed over, then filled again) would not re-render.
  useEffect(() => {
    const node   = wrapper.current
    const onFill = event => {
      flushSync(() => setFilled(null))
      setFilled(event.detail)
    }
    node?.addEventListener('address:fill', onFill)
    return () => node?.removeEventListener('address:fill', onFill)
  }, [])

  // onInput: keystrokes under three letters never reach onChange.
  return(
    <div ref={wrapper} onInput={() => setNoMatch(false)}>
      <Autocomplete
        defaultValue    = {filled}
        identifier      = {props.shownValue}
        shownValue      = {props.shownValue}
        fieldId         = {props.fieldId}
        fieldName       = {props.fieldName}
        required        = {props.required}
        placeholder     = {props.placeholder}
        readonly        = {props.readonly}
        datasets        = {props.data}
        preSuggestions  = {suggestions}
        onChange        = {onChange}
        onSelectElement = {setAddress}
        footer          = {ATTRIBUTION}
        isText
      />
      {noMatch && !props.readonly &&
        <div className="autocomplete__hint">{I18n.t('address_autocomplete.no_match')}</div>
      }
    </div>
  )
}

export default Address
